import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Heart, Droplet, RefreshCw } from 'lucide-react';

const BlockItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0">
      <Icon className="w-6 h-6 text-accent" />
    </div>
    <div>
      <h4 className="font-display text-foreground mb-1">{title}</h4>
      <p className="text-muted-foreground font-body text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

export const BlockagesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const blockages = [
    {
      icon: RefreshCw,
      title: "Ton mental ou ton ego qui te protègent",
      description: "(c'est leur rôle !) Et le blocage persistera tant que tes mécanismes de défense ne seront pas contournés et libérés."
    },
    {
      icon: Droplet,
      title: "Des mémoires sont ancrées dans ton corps",
      description: "et elles peuvent se réveiller lorsque quelque chose agit comme un \"déclencheur\"."
    },
    {
      icon: Heart,
      title: "Ton inconscient contient des peurs ou des blessures",
      description: "qu'il réactive à travers des situations qui se répètent et te mettent en échec. C'est un cycle d'auto-sabotage épuisant."
    },
    {
      icon: Sparkles,
      title: "Tu étudies la spiritualité au lieu de la vivre",
      description: "tu connais la théorie par cœur mais tu as de la peine à l'incarner."
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background relative overflow-hidden">
      <div className="container-luxury relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
              Pourquoi tu stagnes
            </span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground leading-tight mb-6">
              Quelque chose peut te freiner à développer ton 
              <span className="text-gradient-gold"> plein potentiel</span>
            </h2>

            <p className="text-muted-foreground font-body mb-8 leading-relaxed">
              Tu as certainement déjà fait un travail sur toi. Thérapie, hypnose, reiki, yoga, stages de 
              développement personnel… Et je t'en félicite ! Mais si tu lis ces lignes, c'est sûrement que 
              tu sens encore en toi un blocage ou un manque.
            </p>

            <div className="space-y-6">
              {blockages.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                >
                  <BlockItem {...item} />
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_soin_energetique_initiation_avec_les_dauphins_multid_dbedaed2-4935-4298-9aa6-7f5d969c568c-650x650.png"
              alt="Initiation avec les dauphins"
              className="relative rounded-3xl w-full border border-accent/20 shadow-gold"
            />
          </motion.div>
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="glass-gold rounded-3xl p-8 md:p-12 max-w-3xl mx-auto">
            <h3 className="font-display text-2xl text-foreground mb-4">
              Et c'est là que la "magie" des fréquences delphiques entre en jeu.
            </h3>
            <p className="text-accent text-xl font-body">
              Reset 0 : ces fréquences quantiques te remettent dans ton axe de vie
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default BlockagesSection;
